import { useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Button as MuiButton, Tooltip } from "@mui/material";
import ConfirmRemovePropertyModal from "../../../common/ConfirmRemovePropertyModal";
import { TRealEstateData, TTrustsData } from "../../../../utilities/trustTypes";
import { getDefaultRealEstateData, isRealEstateEmpty } from "../../../../utilities/defaultDataTypes";
import styled from "styled-components";

const StyledButton = styled(MuiButton)`
  && {
    background-color: #656e51;
    color: #f5f4ed;
    font-size: 1rem;
    padding: 6px 12px;
    text-transform: capitalize;
    width: auto;

    &:hover {
      background-color: #4c5637;
    }
  }
`;

const StyledHeadlineH5 = styled.h5`
  color: #4c5637;
  font-weight: bold;
`;

const StyledSecondaryH5 = styled.h5`
  color: #656e51;
  font-weight: bold;
  font-size: 18px;
`;

export const defaultRealEstateData: TRealEstateData =
  getDefaultRealEstateData();

export default function CPTRealEstate({
  formData,
  setFormData,
  readOnly,
}: {
  formData: TTrustsData;
  setFormData: (trustsData: TTrustsData) => void;
  readOnly?: boolean;
}) {
  const updateRealEstate = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    index: number,
    key: keyof TRealEstateData,
  ) => {
    if (readOnly) return;
    let value: string | boolean = event.currentTarget.value;
    if (value === "true") value = true;
    else if (value === "false") value = false;
    const updated = formData.realEstate.map((re, i) =>
      i === index ? { ...re, [key]: value } : re
    );
    setFormData({ ...formData, realEstate: updated });
  };

  const setPrimary = (index: number) => {
    if (readOnly) return;
    const updated = formData.realEstate.map((re, i) => ({
      ...re,
      isPrimary: i === index,
    }));
    setFormData({ ...formData, realEstate: updated });
  };

  const addProperty = () => {
    const isFirst = formData.realEstate.length === 0;
    setFormData({
      ...formData,
      realEstate: [...formData.realEstate, { ...getDefaultRealEstateData(), isPrimary: isFirst }],
    });
  };

  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

  const removeProperty = (index: number) => {
    setFormData({
      ...formData,
      realEstate: formData.realEstate.filter((_, i) => i !== index),
    });
  };

  const confirmRemove = () => {
    if (pendingRemoveIndex !== null) {
      removeProperty(pendingRemoveIndex);
      setPendingRemoveIndex(null);
    }
  };

  return (
    <>
      {formData.realEstate.length === 0 && (
        <p>No properties added. Click "Add Property" to add one.</p>
      )}
      {formData.realEstate.map((re, index) => (
        <div key={index}>
          <Row className="mb-2 align-items-center">
            {formData.realEstate.length > 1 && (
              <Col>
                <StyledHeadlineH5>Property {index + 1}</StyledHeadlineH5>
              </Col>
            )}
            {!readOnly && (
              <Col xs="auto" className={formData.realEstate.length === 1 ? "ms-auto" : ""}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => isRealEstateEmpty(re) ? removeProperty(index) : setPendingRemoveIndex(index)}
                >
                  Remove
                </Button>
              </Col>
            )}
          </Row>
          <Row>
            <Col xs={4}>
              <Form.Group className="mb-3" controlId={`street-${index}`}>
                <Form.Label>Street Address</Form.Label>
                <Form.Control
                  name="street"
                  type="text"
                  value={re.street}
                  onChange={(e) => updateRealEstate(e, index, "street")}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId={`city-${index}`}>
                <Form.Label>City</Form.Label>
                <Form.Control
                  name="city"
                  type="text"
                  value={re.city}
                  onChange={(e) => updateRealEstate(e, index, "city")}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId={`state-${index}`}>
                <Form.Label>State</Form.Label>
                <Form.Control
                  name="state"
                  type="text"
                  value={re.state}
                  onChange={(e) => updateRealEstate(e, index, "state")}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId={`zip-${index}`}>
                <Form.Label>Zip</Form.Label>
                <Form.Control
                  name="zip"
                  type="text"
                  value={re.zip}
                  onChange={(e) => updateRealEstate(e, index, "zip")}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={3}>
              <Form.Group className="mb-3" controlId={`county-${index}`}>
                <Form.Label>County</Form.Label>
                <Form.Control
                  name="county"
                  type="text"
                  value={re.county}
                  onChange={(e) => updateRealEstate(e, index, "county")}
                />
              </Form.Group>
            </Col>
            <Col xs={3}>
              <Form.Group className="mb-3" controlId={`apn-${index}`}>
                <Form.Label>Property APN</Form.Label>
                <Form.Control
                  name="apn"
                  type="text"
                  value={re.apn}
                  onChange={(e) => updateRealEstate(e, index, "apn")}
                />
              </Form.Group>
            </Col>
            <Col xs="auto" className="d-flex align-items-end pb-3">
              <Tooltip
                title="The primary property is used for CPT document generation"
                placement="top"
                arrow
              >
                <span>
                  <Form.Check
                    type="radio"
                    id={`primary-${index}`}
                    label="Primary"
                    checked={re.isPrimary}
                    onChange={() => setPrimary(index)}
                    disabled={readOnly}
                  />
                </span>
              </Tooltip>
            </Col>
          </Row>
          <hr />
          <Row className="mb-3">
            <StyledHeadlineH5>
              {formData.realEstate.length > 1 ? `Property ${index + 1} Deed Information` : "Deed Information"}
            </StyledHeadlineH5>
          </Row>
          <Row className="mb-3">
            <StyledSecondaryH5>Grantors</StyledSecondaryH5>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId={`grantor1-${index}`}>
                <Form.Label>Name of Grantor 1 on Deed</Form.Label>
                <Form.Control
                  name="grantor1"
                  type="text"
                  value={re.grantor1}
                  onChange={(e) => updateRealEstate(e, index, "grantor1")}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId={`grantor2-${index}`}>
                <Form.Label>Name of Grantor 2 on Deed</Form.Label>
                <Form.Control
                  name="grantor2"
                  type="text"
                  value={re.grantor2}
                  onChange={(e) => updateRealEstate(e, index, "grantor2")}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId={`grantorTitle-${index}`}>
                <Form.Label>Title of Grantors on Deed</Form.Label>
                <Form.Control
                  name="grantorTitle"
                  type="text"
                  value={re.grantorTitle}
                  onChange={(e) => updateRealEstate(e, index, "grantorTitle")}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <StyledSecondaryH5>Property</StyledSecondaryH5>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId={`description-${index}`}>
                <Form.Label>Property Description</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  value={re.description}
                  onChange={(e) => updateRealEstate(e, index, "description")}
                />
              </Form.Group>
            </Col>
          </Row>
          {index < formData.realEstate.length - 1 && <hr />}
        </div>
      ))}
      {!readOnly && (
        <Row className="justify-content-center">
          <StyledButton onClick={addProperty}>Add Property</StyledButton>
        </Row>
      )}
      <Modal
        centered
        show={pendingRemoveIndex !== null}
        onHide={() => setPendingRemoveIndex(null)}
        backdrop="static"
        keyboard={false}
        size="sm"
      >
        <ConfirmRemovePropertyModal
          onConfirm={confirmRemove}
          onCancel={() => setPendingRemoveIndex(null)}
        />
      </Modal>
    </>
  );
}
